/**
 * Authentication Context
 * Provides authentication state and methods throughout the app
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../config/authConfig';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userRole, setUserRole] = useState(() => {
    // Load role from localStorage on init
    return localStorage.getItem('leading_user_role') || 'participant';
  });

  useEffect(() => {
    const initializeUser = () => {
      console.log('=== AUTH CONTEXT: Initializing user ===');
      console.log('Accounts length:', accounts.length);
      
      if (accounts.length > 0) {
        const account = accounts[0];
        console.log('Account info:', {
          name: account.name,
          email: account.username,
          userId: account.localAccountId
        });
        
        const userData = {
          name: account.name,
          email: account.username,
          userId: account.localAccountId,
          roles: [userRole === 'admin' ? 'Admin' : 'Participant'],
        };
        
        console.log('User initialized:', userData);
        
        setUser(userData);
        setIsAuthenticated(true);
      } else {
        console.log('No accounts found, clearing user state');
        setUser(null);
        setIsAuthenticated(false);
      }
      setIsLoading(false);
    };
    
    initializeUser();
  }, [accounts, userRole]);

  const login = async () => {
    try {
      await instance.loginPopup(loginRequest);
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await instance.logoutPopup({
        postLogoutRedirectUri: window.location.origin,
      });
      setUser(null);
      setIsAuthenticated(false);
    } catch (error) {
      console.error('Logout failed:', error);
      throw error;
    }
  };

  const getAccessToken = async () => {
    if (accounts.length === 0) {
      throw new Error('No active account');
    }

    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      });
      return response.accessToken;
    } catch (error) {
      console.error('Silent token acquisition failed:', error);
      // Fallback to interactive method
      try {
        const response = await instance.acquireTokenPopup(loginRequest);
        return response.accessToken;
      } catch (popupError) {
        console.error('Interactive token acquisition failed:', popupError);
        throw popupError;
      }
    }
  };

  const toggleRole = () => {
    const newRole = userRole === 'admin' ? 'participant' : 'admin';
    console.log('Toggling role from', userRole, 'to', newRole);
    setUserRole(newRole);
    localStorage.setItem('leading_user_role', newRole);
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    getAccessToken,
    inProgress,
    userRole,
    toggleRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
